import type { GetWebcastsQuery } from '../../../../generated/graphql-manager';

import { Modal, Paragraph, PrimaryButton, SecondaryButton } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { GetWebcastsDocument as GET_WEBCASTS, useArchiveWebcastMutation } from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../hooks/current-user';

type Props = {
  webcastId: string;
  isOpen: boolean;
  onClose: () => void;
};

export function ArchiveWebcastModal({ webcastId, isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const [error, setError] = useState(false);

  const routes = useAbsoluteRoutes();
  const { pathname } = useLocation();
  const isEventSummaryPage = pathname.includes(routes.eventSummary);

  const [archiveWebcastMutation, { loading }] = useArchiveWebcastMutation();

  const handleConfirm = () => {
    if (loading || !webcastId) return;

    archiveWebcastMutation({
      variables: { webcastId },
      optimisticResponse: {
        archiveWebcast: {
          __typename: 'ArchiveWebcastSuccess',
          webcast: {
            __typename: 'Webcast',
            id: webcastId,
          },
        },
      },
      update: (cache, { data }) => {
        if (isEventSummaryPage) return;

        if (data?.archiveWebcast.__typename !== 'ArchiveWebcastSuccess') return;

        const cachedWebcastsQuery = cache.readQuery<GetWebcastsQuery>({
          query: GET_WEBCASTS,
          variables: { lsproId },
        });

        const updatedCachedWebcasts = cachedWebcastsQuery?.webcasts.edges.filter(
          (edge) => edge?.node?.id !== webcastId
        );

        cache.writeQuery({
          query: GET_WEBCASTS,
          variables: { lsproId },
          data: {
            webcasts: {
              ...cachedWebcastsQuery?.webcasts,
              edges: updatedCachedWebcasts,
            },
          },
        });

        cache.evict({ id: cache.identify({ id: webcastId, __typename: 'Webcast' }) });
        cache.gc();
      },
      onCompleted: (data) => {
        if (data.archiveWebcast.__typename === 'ArchiveWebcastProblem') {
          setError(true);
        } else {
          onClose();
        }
      },
      onError: () => {
        setError(true);
      },
    });
  };

  useEffect(() => {
    if (webcastId) setError(false);
  }, [webcastId]);

  return (
    <Modal
      data-testid="archive-webcast-modal"
      title={t('manager.webcasts.archiveWebcastModal.title')}
      loading={loading}
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <>
          <SecondaryButton data-testid="cancel-archive-webcast-button" type="button" onClick={onClose}>
            {error ? t('common.close') : t('common.cancel')}
          </SecondaryButton>

          {!error && (
            <PrimaryButton
              data-testid="confirm-archive-webcast-button"
              type="button"
              onClick={handleConfirm}
              disabled={loading}
            >
              {t('manager.webcasts.archiveWebcastModal.archiveButton')}
            </PrimaryButton>
          )}
        </>
      }
    >
      <Paragraph data-testid="archive-webcast-modal-info">
        {error ? t('manager.webcasts.archiveWebcastModal.errorInfo') : t('manager.webcasts.archiveWebcastModal.info')}
      </Paragraph>
    </Modal>
  );
}
